import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Text } from "@nbs/ui";
import { HUB_COLLECT_CATALOG_QUERY } from "../api/documents";
import { HubPageTitle } from "../components/HubPageTitle";

const CYCLE_TONES = ["collect", "verify", "validate", "gender", "restore", "adjust"] as const;

/** Meta / parent SE rows — not selectable as a collection service. */
const EXCLUDED_META_SE = new Set([
  "nbs-se-approvisionnement",
  "nbs-se-services-culturels",
  "nbs-se-valorisation-economique",
  "nbs-se-perceptions",
  "nbs-se-territoire",
  "nbs-se-indicateurs-socio-economiques-transversaux",
  "nbs-se-pressions-menaces",
  "nbs-se-objectifs-developpement-durable",
]);

const CATEGORY_LABELS: Record<string, string> = {
  approvisionnement: "Approvisionnement",
  culturel: "Culturel",
  regulation: "Régulation",
  "socio-economique-transversal": "Indicateurs socio-économiques transversaux",
  "pressions-menaces": "Pressions et menaces",
  "objectifs-developpement-durable": "Objectifs de développement durable",
  "socio-culturel": "Indicateurs socio-économiques transversaux",
};

type CollectService = {
  readonly id: string;
  readonly libelle: string;
  readonly categorie?: string | null;
};

type CollectIndicator = {
  readonly id: string;
  readonly libelle: string;
  readonly serviceId?: string | null;
  readonly unite?: string | null;
};

function asStringList(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function asCycleSteps(
  value: unknown,
): readonly { readonly title: string; readonly detail: string }[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const title = "title" in item && typeof item.title === "string" ? item.title : "";
    const detail = "detail" in item && typeof item.detail === "string" ? item.detail : "";
    return title ? [{ title, detail }] : [];
  });
}

function categoryLabel(code: string): string {
  return CATEGORY_LABELS[code] ?? code;
}

/**
 * Field-collection prototype plus the synthetic reading of report §5.3.9.5
 * (community collection → local validation → scientific centralization →
 * gender analysis → public restitution → management adjustment).
 */
export function HubCollectPage() {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useQuery(HUB_COLLECT_CATALOG_QUERY, {
    fetchPolicy: "cache-and-network",
  });
  const [serviceId, setServiceId] = useState("");
  const [indicateurId, setIndicateurId] = useState("");
  const [value, setValue] = useState("");
  const [pressure, setPressure] = useState("exploitation-bois");
  const [saved, setSaved] = useState(false);
  const [gps] = useState(() => ({
    lat: 9.72,
    lng: -12.68,
  }));

  const services = useMemo(() => {
    const list = (data?.indicatorFilterOptions?.services ?? []) as CollectService[];
    return list
      .filter((service) => !EXCLUDED_META_SE.has(service.id))
      .slice()
      .sort((a, b) => a.libelle.localeCompare(b.libelle, "fr", { sensitivity: "base" }));
  }, [data]);

  const servicesByCategory = useMemo(() => {
    const groups = new Map<string, CollectService[]>();
    for (const service of services) {
      const category = (service.categorie ?? "").trim() || "autre";
      const bucket = groups.get(category) ?? [];
      bucket.push(service);
      groups.set(category, bucket);
    }
    return [...groups.entries()].sort((a, b) =>
      categoryLabel(a[0]).localeCompare(categoryLabel(b[0]), "fr", { sensitivity: "base" }),
    );
  }, [services]);

  const indicators = useMemo(() => {
    const nodes = (data?.indicatorCatalog?.nodes ?? []) as CollectIndicator[];
    if (!serviceId) return [];
    return nodes
      .filter((node) => node.serviceId === serviceId)
      .slice()
      .sort((a, b) => a.libelle.localeCompare(b.libelle, "fr", { sensitivity: "base" }));
  }, [data, serviceId]);

  useEffect(() => {
    if (!services.length) return;
    if (!serviceId || !services.some((service) => service.id === serviceId)) {
      setServiceId(services[0]!.id);
    }
  }, [services, serviceId]);

  useEffect(() => {
    if (!indicators.length) {
      if (indicateurId) setIndicateurId("");
      return;
    }
    if (!indicators.some((indicator) => indicator.id === indicateurId)) {
      setIndicateurId(indicators[0]!.id);
    }
  }, [indicators, indicateurId]);

  const objectives = asStringList(t("hub.collect.objectives", { returnObjects: true }));
  const gesi = asStringList(t("hub.collect.gesi", { returnObjects: true }));
  const principles = asStringList(t("hub.collect.principles", { returnObjects: true }));
  const cycle = asCycleSteps(t("hub.collect.cycle", { returnObjects: true }));
  const selectedIndicator = indicators.find((indicator) => indicator.id === indicateurId);
  const catalogReady = !loading || services.length > 0;

  return (
    <div className="nbs-hub-page" data-testid="hub-collect-page">
      <section className="nbs-hub-section">
        <HubPageTitle>{t("hub.collect.title")}</HubPageTitle>
        <p className="nbs-hub-collect__lead">
          {t("hub.collect.lead")}{" "}
          <Link className="nbs-hub-collect__report-link" to="/hub/rapport">
            {t("hub.collect.reportLink")}
          </Link>
        </p>

        {error ? (
          <div className="nbs-hub-banner nbs-hub-banner--danger" role="alert">
            <Text>{t("hub.collect.catalogError")}</Text>
            <button type="button" className="nbs-hub-btn" onClick={() => void refetch()}>
              {t("hub.overview.retry")}
            </button>
          </div>
        ) : null}

        <div className="nbs-hub-collect">
          <article
            className="nbs-hub-card nbs-hub-collect__phone"
            aria-label="Formulaire de collecte"
          >
            <header className="nbs-hub-collect__phone-head">
              <p>Saisie terrain</p>
              <span>Hors ligne prêt</span>
            </header>

            <label>
              Service
              <select
                data-testid="hub-collect-service"
                value={serviceId}
                disabled={!catalogReady || services.length === 0}
                onChange={(e) => {
                  setServiceId(e.target.value);
                  setSaved(false);
                }}
              >
                {!catalogReady ? <option value="">{t("hub.collect.loadingCatalog")}</option> : null}
                {servicesByCategory.map(([category, items]) => (
                  <optgroup key={category} label={categoryLabel(category)}>
                    {items.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.libelle}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>

            <label>
              Indicateur du service écosystémique
              <select
                data-testid="hub-collect-indicateur"
                value={indicateurId}
                disabled={!catalogReady || indicators.length === 0}
                onChange={(e) => {
                  setIndicateurId(e.target.value);
                  setSaved(false);
                }}
              >
                {indicators.length === 0 ? (
                  <option value="">{t("hub.collect.noIndicators")}</option>
                ) : (
                  indicators.map((indicator) => (
                    <option key={indicator.id} value={indicator.id}>
                      {indicator.unite?.trim()
                        ? `${indicator.libelle} (${indicator.unite.trim()})`
                        : indicator.libelle}
                    </option>
                  ))
                )}
              </select>
            </label>

            <label>
              Valeur observée
              <input
                type="number"
                inputMode="decimal"
                placeholder={
                  selectedIndicator?.unite?.trim()
                    ? `ex. 12.5 ${selectedIndicator.unite.trim()}`
                    : "ex. 12.5"
                }
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setSaved(false);
                }}
              />
            </label>

            <label>
              Pressions / Menaces
              <select value={pressure} onChange={(e) => setPressure(e.target.value)}>
                <option value="exploitation-bois">Exploitation de bois</option>
                <option value="agriculture">Extension agricole</option>
                <option value="habitat">Occupation habité</option>
                <option value="infra">Infrastructures</option>
                <option value="aucune">Aucune pression signalée</option>
              </select>
            </label>

            <label>
              Intensité perçue
              <select defaultValue="moyenne">
                <option value="faible">Faible</option>
                <option value="moyenne">Moyenne</option>
                <option value="forte">Forte</option>
              </select>
            </label>

            <label>
              Coordonnées GPS
              <input
                readOnly
                value={`${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)}`}
                aria-describedby="hub-collect-gps-hint"
              />
            </label>
            <p id="hub-collect-gps-hint" className="nbs-hub-muted">
              Position simulée (Madina-Oula) — en production, capturée par l’appareil.
            </p>

            <button
              type="button"
              className="nbs-hub-btn nbs-hub-btn--block"
              onClick={() => setSaved(true)}
            >
              Enregistrer hors ligne
            </button>

            {saved ? (
              <p className="nbs-hub-collect__toast" role="status">
                Brouillon local enregistré (prototype). Aucune écriture catalogue.
              </p>
            ) : null}
          </article>

          <div className="nbs-hub-collect__strategy">
            <aside className="nbs-hub-card">
              <header className="nbs-hub-card__head">
                <h3>{t("hub.collect.objectivesTitle")}</h3>
              </header>
              <ul className="nbs-hub-collect__points">
                {objectives.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </aside>

            <aside className="nbs-hub-card" data-testid="hub-collect-gesi">
              <header className="nbs-hub-card__head">
                <h3>{t("hub.collect.gesiTitle")}</h3>
              </header>
              <div className="nbs-hub-collect__prose">
                {gesi.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </aside>

            <aside className="nbs-hub-card">
              <header className="nbs-hub-card__head">
                <h3>{t("hub.collect.principlesTitle")}</h3>
              </header>
              <ul className="nbs-hub-collect__points">
                {principles.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </aside>

            <aside className="nbs-hub-card">
              <header className="nbs-hub-card__head">
                <h3>{t("hub.collect.cycleTitle")}</h3>
                <p>{t("hub.collect.cycleLead")}</p>
              </header>
              <ol className="nbs-hub-lifecycle">
                {cycle.map((step, index) => (
                  <li key={step.title} data-tone={CYCLE_TONES[index] ?? "collect"}>
                    <strong>{step.title}</strong>
                    <span>{step.detail}</span>
                  </li>
                ))}
              </ol>
              <p className="nbs-hub-muted">{t("hub.collect.reportHint")}</p>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
